/// Field descriptors for a creator-registry entry. **This array is the
/// extension point**: the Manage-organizations form renders by looping it, so
/// surfacing a new schema.org property is one entry here plus one string in
/// `ORG_EXTRA_KEYS` server-side — no migration, no new component.
///
/// `core: true` fields are typed columns with their own validation and are what
/// the export contract and entity resolution depend on. Everything else lives
/// in the `extra` JSONB map. That split is deliberate: an undeclared `name` is
/// exactly how a credit sentence reached an entity slot.
export interface OrgFieldDescriptor {
  /// Column name (core) or `extra` key, spelled as schema.org spells it.
  key: string;
  label: string;
  kind: "select" | "text" | "url";
  core: boolean;
  options?: string[];
  required?: boolean;
  placeholder?: string;
  help?: string;
}

export const ORG_FIELDS: OrgFieldDescriptor[] = [
  {
    key: "entity_type",
    label: "Type",
    kind: "select",
    core: true,
    options: ["Organization", "Person"],
    required: true,
  },
  {
    key: "name",
    label: "Name",
    kind: "text",
    core: true,
    required: true,
    placeholder: "International Organization for Standardization",
    help: "The entity's name. A licence's credit line belongs in the dataset's Attribution instead.",
  },
  {
    key: "url",
    label: "Homepage",
    kind: "url",
    core: true,
    placeholder: "https://www.iso.org/",
    help: "The entity's own site, not the dataset's page.",
  },
  {
    key: "same_as",
    label: "Wikidata / Wikipedia",
    kind: "url",
    core: true,
    placeholder: "https://www.wikidata.org/wiki/Q15028",
    help: "Highest-value field: this is what lets Google resolve the entity instead of just printing its name. Most valuable for standards bodies.",
  },
  {
    key: "alternateName",
    label: "Also known as",
    kind: "text",
    core: false,
    placeholder: "ISO",
  },
  { key: "legalName", label: "Legal name", kind: "text", core: false },
  {
    key: "foundingDate",
    label: "Founded",
    kind: "text",
    core: false,
    placeholder: "1947-02-23",
  },
  { key: "logo", label: "Logo URL", kind: "url", core: false },
  { key: "email", label: "Email", kind: "text", core: false },
  {
    key: "identifier",
    label: "Identifier",
    kind: "text",
    core: false,
    help: "An external id (GRID, VIAF, ROR…) when there is no Wikidata entry.",
  },
  {
    key: "parentOrganization",
    label: "Parent organisation",
    kind: "text",
    core: false,
  },
];

export const MAX_ENTITY_NAME_LEN = 100;

/// Mirrors the server's `entity_name_looks_valid`: non-empty, within the length
/// cap, no shape heuristics.
export const entityNameLooksValid = (name: string): boolean => {
  const t = name.trim();
  return t.length > 0 && t.length <= MAX_ENTITY_NAME_LEN;
};
